<template>
  <Teleport to="body">
    <div v-if="visible" class="toast-container">
      <div class="toast" :class="toastClass" :style="toastStyle">
        <span class="toast-icon">{{ icon }}</span>
        <span class="toast-message">{{ message }}</span>
        <button v-if="dismissible" class="toast-close" @click="dismiss" aria-label="Close">
          ×
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

const props = defineProps({
  message: { type: String, required: true },
  type: {
    type: String,
    default: 'info',
    validator: v => ['success', 'error', 'warning', 'info'].includes(v),
  },
  duration: { type: Number, default: 5000 },
  dismissible: { type: Boolean, default: true },
})

const emit = defineEmits(['dismiss'])

const visible = ref(false)

const toastClass = computed(() => `toast-${props.type}`)

const toastStyle = computed(() => ({
  animation: visible.value ? 'slideIn 0.3s ease-out' : 'slideOut 0.3s ease-in',
}))

const icon = computed(() => {
  switch (props.type) {
    case 'success':
      return '✓'
    case 'error':
      return '✕'
    case 'warning':
      return '⚠'
    default:
      return 'ℹ'
  }
})

let timeoutId = null

function show() {
  visible.value = true
  if (props.duration > 0) {
    timeoutId = setTimeout(() => {
      dismiss()
    }, props.duration)
  }
}

function dismiss() {
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  visible.value = false
  setTimeout(() => {
    emit('dismiss')
  }, 300) // Wait for animation
}

onMounted(() => {
  show()
})

defineExpose({
  show,
  dismiss,
})
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  max-width: 500px;
  pointer-events: auto;
  background: #fff;
  border: 1px solid #e0e0e0;
}

.toast-success {
  background: #d1fae5;
  color: #065f46;
  border-color: #6ee7b7;
}

.toast-error {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fca5a5;
}

.toast-warning {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}

.toast-info {
  background: #dbeafe;
  color: #1e40af;
  border-color: #93c5fd;
}

.toast-icon {
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
}

.toast-message {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
}

.toast-close {
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.toast-close:hover {
  opacity: 1;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}
</style>
